// src/pages/subscription/components/CardPayment.jsx
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CreditCard, Shield, Loader, AlertCircle, CheckCircle } from 'lucide-react';
import { useLocalization } from '../../../contexts/LocalizationContext';
import { useAlert } from '../../../contexts/AlertContext';
import { formatPrice } from '../../../utils/subscriptionUtils';
import styles from './CardPayment.module.css';

const CardPayment = ({ 
  paymentParams, 
  onSuccess, 
  onCancel, 
  onError 
}) => {
  const { t } = useLocalization();
  const { success, error: showError } = useAlert();
  const navigate = useNavigate();
  const [isProcessing, setIsProcessing] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState(null);

  useEffect(() => {
    // Check if PayHere SDK is loaded
    if (!window.payhere) {
      console.error('PayHere SDK not loaded');
      showError(t('subscription.payment.payhereNotLoaded'), {
        title: t('common.error')
      });
      return;
    }

    // Setup PayHere callbacks
    window.payhere.onCompleted = function(orderId) {
      console.log('Payment completed. OrderID:', orderId);
      setPaymentStatus('completed');
      setIsProcessing(false);
      
      success(t('subscription.payment.paymentSuccessMessage'), {
        title: t('subscription.payment.paymentSuccess'),
        duration: 5000
      });

      // Call success callback
      if (onSuccess) {
        onSuccess(orderId);
      }
    };

    window.payhere.onDismissed = function() {
      console.log('Payment dismissed');
      setPaymentStatus('dismissed');
      setIsProcessing(false);
      
      showError(t('subscription.payment.paymentDismissed'), {
        title: t('common.warning')
      });

      // Call cancel callback
      if (onCancel) {
        onCancel();
      }
    };

    window.payhere.onError = function(error) {
      console.error('Payment error:', error);
      setPaymentStatus('error');
      setIsProcessing(false);
      
      showError(t('subscription.payment.paymentFailedMessage'), {
        title: t('subscription.payment.paymentFailed')
      });

      // Call error callback
      if (onError) {
        onError(error);
      }
    };

    // Cleanup
    return () => {
      window.payhere.onCompleted = null;
      window.payhere.onDismissed = null;
      window.payhere.onError = null;
    };
  }, [t, success, showError, onSuccess, onCancel, onError]);

  const handlePayNow = () => {
    if (!window.payhere) {
        showError(t('subscription.payment.payhereNotLoaded'), {
        title: t('common.error')
        });
        return;
    }

    if (!paymentParams || !paymentParams.hash) {
        showError(t('subscription.payment.invalidPaymentParams'), {
        title: t('common.error')
        });
        return;
    }

    setIsProcessing(true);
    setPaymentStatus('processing');

    try {
        // Prepare payment object for PayHere
        const payment = {
        sandbox: paymentParams.sandbox !== undefined ? paymentParams.sandbox : true,
        merchant_id: paymentParams.merchant_id,
        return_url: paymentParams.return_url || undefined,
        cancel_url: paymentParams.cancel_url || undefined,
        notify_url: paymentParams.notify_url,
        order_id: paymentParams.order_id,
        items: paymentParams.items,
        amount: parseFloat(paymentParams.amount).toFixed(2),
        currency: paymentParams.currency,
        first_name: paymentParams.first_name,
        last_name: paymentParams.last_name,
        email: paymentParams.email,
        phone: paymentParams.phone,
        address: paymentParams.address,
        city: paymentParams.city,
        country: paymentParams.country,
        hash: paymentParams.hash,
        };

        // Add optional fields if present
        if (paymentParams.custom_1) {
        payment.custom_1 = paymentParams.custom_1;
        }
        if (paymentParams.custom_2) {
        payment.custom_2 = paymentParams.custom_2;
        }

        console.log('Starting PayHere payment with params:', payment);

        // Start PayHere payment
        window.payhere.startPayment(payment);
    } catch (err) {
        console.error('Error starting payment:', err);
        setIsProcessing(false);
        setPaymentStatus('error');
        showError(t('subscription.payment.paymentInitFailed'), {
        title: t('common.error')
        });
    }
    };

  if (!paymentParams) {
    return (
      <div className={styles.cardPayment}>
        <div className={styles.errorState}>
          <AlertCircle size={48} className={styles.errorIcon} />
          <h3>{t('common.error')}</h3>
          <p>{t('subscription.payment.invalidPaymentParams')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.cardPayment}>
      {/* Payment Summary */}
      <div className={styles.paymentSummary}>
        <div className={styles.summaryHeader}>
          <CreditCard size={24} />
          <h3>{t('subscription.payment.paymentSummary')}</h3>
        </div>

        <div className={styles.summaryDetails}>
          <div className={styles.summaryRow}>
            <span className={styles.summaryLabel}>{t('subscription.planName')}</span>
            <span className={styles.summaryValue}>{paymentParams.items}</span>
          </div>

          <div className={styles.summaryRow}>
            <span className={styles.summaryLabel}>{t('subscription.billingCycle')}</span>
            <span className={styles.summaryValue}>
              {paymentParams.billingCycle 
                ? t(`subscription.${paymentParams.billingCycle}`) 
                : t('subscription.monthly')}
            </span>
          </div>

          <div className={`${styles.summaryRow} ${styles.totalRow}`}>
            <span className={styles.summaryLabel}>{t('subscription.payment.totalAmount')}</span>
            <span className={styles.summaryAmount}>
              {formatPrice(paymentParams.amount, paymentParams.currency)}
            </span>
          </div>
        </div>
      </div>

      {/* Customer Details */}
      <div className={styles.customerDetails}>
        <h4>{t('subscription.payment.customerDetails')}</h4>
        <div className={styles.customerGrid}>
          <div className={styles.customerField}>
            <span className={styles.fieldLabel}>{t('subscription.payment.name')}</span>
            <span className={styles.fieldValue}>
              {paymentParams.first_name} {paymentParams.last_name}
            </span>
          </div>
          <div className={styles.customerField}>
            <span className={styles.fieldLabel}>{t('subscription.payment.email')}</span>
            <span className={styles.fieldValue}>{paymentParams.email}</span>
          </div>
          <div className={styles.customerField}>
            <span className={styles.fieldLabel}>{t('subscription.payment.phone')}</span>
            <span className={styles.fieldValue}>{paymentParams.phone}</span>
          </div>
        </div>
      </div>

      {/* Security Badge */}
      <div className={styles.securityBadge}>
        <Shield size={20} />
        <span>{t('subscription.payment.securePayment')}</span>
      </div>

      {/* Payment Status */}
      {paymentStatus === 'processing' && (
        <div className={styles.statusCard}>
          <Loader className={styles.statusIcon} size={24} />
          <p>{t('subscription.payment.processingPayment')}</p>
        </div>
      )}

      {paymentStatus === 'completed' && (
        <div className={`${styles.statusCard} ${styles.successCard}`}>
          <CheckCircle className={styles.statusIcon} size={24} />
          <p>{t('subscription.payment.paymentSuccessMessage')}</p>
        </div>
      )}

      {paymentStatus === 'error' && (
        <div className={`${styles.statusCard} ${styles.errorCard}`}>
          <AlertCircle className={styles.statusIcon} size={24} />
          <p>{t('subscription.payment.paymentFailedMessage')}</p>
        </div>
      )}

      {/* Pay Button */}
      <div className={styles.paymentActions}>
        <button
          type="button"
          className={styles.payButton}
          onClick={handlePayNow}
          disabled={isProcessing || paymentStatus === 'completed'}
        >
          {isProcessing ? (
            <>
              <Loader className={styles.buttonSpinner} size={18} />
              {t('subscription.payment.processingPayment')}
            </>
          ) : paymentStatus === 'completed' ? (
            <>
              <CheckCircle size={18} />
              {t('subscription.payment.completed')}
            </>
          ) : (
            <>
              <CreditCard size={18} />
              {t('subscription.payment.payNow')}
            </>
          )}
        </button>
      </div>

      {/* Terms Notice */}
      <p className={styles.termsNotice}>
        {t('subscription.payment.termsNotice')}
      </p>
    </div>
  );
};

export default CardPayment;